'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { Search, Star, Globe, Trophy, Shield, ChevronDown } from 'lucide-react'
import { useFetchLeagues } from '@/lib/api/league-routes'
import type { League } from '@/lib/types/leagues'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const FEATURED_LEAGUE_IDS = [1, 2, 3, 4, 5]

type ContinentBucket = 'europe' | 'americas' | 'africa' | 'asia'

// Domestic leagues/cups carry a country (and therefore a continent)
// directly. Continental/international competitions usually don't — their
// confederation is only implied by the name (UEFA, CAF, AFC, ...), so fall
// back to a keyword guess for those.
function continentBucket(league: League): ContinentBucket | null {
  const continent = league.country?.continent
  if (continent === 'Europe') return 'europe'
  if (continent === 'Africa') return 'africa'
  if (continent === 'North America' || continent === 'South America') return 'americas'
  if (continent === 'Asia' || continent === 'Oceania') return 'asia'

  const name = league.league_name
  if (/UEFA|Europe|European/i.test(name)) return 'europe'
  if (/CONCACAF|CONMEBOL|Libertadores|Sudamericana|CONMEBOL|Copa America|America/i.test(name)) return 'americas'
  if (/CAF|Africa/i.test(name)) return 'africa'
  if (/AFC|Asia|Asian/i.test(name)) return 'asia'
  return null
}

// Domestic: an actual domestic league season (not a cup).
function isDomestic(l: League) {
  return l.scope === 'domestic' && l.competiton_level === 'club' && l.format === 'league'
}
// Cups: domestic cup / super cup competitions.
function isDomesticCup(l: League) {
  return l.scope === 'domestic' && (l.format === 'cup' || l.format === 'super cup')
}
// International Cups: any national-team competition, or a club competition
// run at international (non-continental) scope.
function isInternationalCup(l: League) {
  return l.competiton_level === 'national' || (l.scope === 'international' && l.competiton_level === 'club')
}
// Continental Cups: continental club competitions (Champions League, Copa
// Libertadores, ...).
function isContinentalCup(l: League) {
  return l.scope === 'continental' && l.competiton_level === 'club'
}

type PillKey = 'all' | 'domestic' | 'cups' | 'international' | 'europe' | 'africa' | 'americas' | 'asia' | 'continental'

const PILLS: { key: PillKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'domestic', label: 'Domestic' },
  { key: 'cups', label: 'Cups' },
  { key: 'international', label: 'International Cups' },
  { key: 'europe', label: 'Europe' },
  { key: 'africa', label: 'Africa/Middle East' },
  { key: 'americas', label: 'Americas' },
  { key: 'asia', label: 'Asia' },
  { key: 'continental', label: 'Continental Cups' },
]

function matchesPill(l: League, pill: PillKey): boolean {
  switch (pill) {
    case 'all': return true
    case 'domestic': return isDomestic(l)
    case 'cups': return isDomesticCup(l)
    case 'international': return isInternationalCup(l)
    case 'continental': return isContinentalCup(l)
    case 'europe': case 'africa': case 'americas': case 'asia':
      return continentBucket(l) === pill
  }
}

function matchesSearch(l: League, q: string): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  return l.league_name.toLowerCase().includes(needle) || (l.country?.name?.toLowerCase().includes(needle) ?? false)
}

// ─── league row (used inside every accordion group) ────────────────────────

function LeagueRow({ league }: { league: League }) {
  return (
    <Link
      href={`/leagues/${league.league_id}`}
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 no-underline transition-colors hover:bg-white/5"
    >
      <span className="flex size-9 shrink-0 items-center justify-center">
        {league.logo_url ? (
          <img src={league.logo_url} alt="" className="size-8 object-contain" />
        ) : (
          <span className="size-8 rounded-full border border-white/10" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-white">{league.league_name}</span>
        <span className="block truncate text-xs text-white/40">{league.country?.name ?? 'International'}</span>
      </span>
    </Link>
  )
}

// ─── accordion group ────────────────────────────────────────────────────────

function LeagueGroupAccordion({
  icon: Icon,
  title,
  leagues,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  leagues: League[]
}) {
  if (leagues.length === 0) return null
  return (
    <AccordionItem value={title} className="rounded-xl border border-white/10 bg-[#0e1114]">
      <AccordionTrigger className="px-4 py-3 no-underline hover:no-underline [&>svg]:hidden">
        <span className="flex w-full items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/5">
            <Icon className="size-4 text-white/70" />
          </span>
          <span className="min-w-0 flex-1 text-left text-sm font-semibold text-white">{title}</span>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-medium text-white/70">
            {leagues.length}
          </span>
          <ChevronDown className="size-4 shrink-0 text-white/40 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
        </span>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-0.5 px-2 pb-2">
        {leagues.map((l) => (
          <LeagueRow key={l.league_id} league={l} />
        ))}
      </AccordionContent>
    </AccordionItem>
  )
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function LeaguesPage() {
  const { data, isLoading } = useFetchLeagues()
  const [pill, setPill] = useState<PillKey>('all')
  const [search, setSearch] = useState('')

  const leagues = useMemo(() => data?.data ?? [], [data])
  const countries = useMemo(
    () => new Set(leagues.map((l) => l.country?.country_id).filter((id): id is number => id != null)),
    [leagues]
  )

  const featured = useMemo(
    () => FEATURED_LEAGUE_IDS.map((id) => leagues.find((l) => l.league_id === id)).filter((l): l is League => !!l),
    [leagues]
  )
  const featuredIds = new Set(FEATURED_LEAGUE_IDS)
  const showFeatured = search.trim().length === 0 && pill === 'all'

  const filtered = useMemo(
    () =>
      leagues.filter(
        (l) => (!showFeatured || !featuredIds.has(l.league_id)) && matchesPill(l, pill) && matchesSearch(l, search)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leagues, pill, search, showFeatured]
  )

  const continentalCups = filtered.filter(isContinentalCup)
  const international = filtered.filter(isInternationalCup)
  const domestic = filtered.filter(isDomestic)
  const domesticCups = filtered.filter(isDomesticCup)

  const byBucket = (list: League[], bucket: ContinentBucket) => list.filter((l) => continentBucket(l) === bucket)

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 py-4">
      <div>
        <h1 className="text-2xl font-bold text-white">Leagues</h1>
        <p className="mt-1 text-sm text-white/50">
          {leagues.length} competitions across {countries.size}+ countries and every continent — stats, standings,
          detailed match data and predictions.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-white/40" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search leagues or countries…"
          className="w-full rounded-md border border-white/10 bg-black py-2.5 pl-10 pr-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/30"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {PILLS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPill(p.key)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              pill === p.key
                ? 'border-orange-500 bg-orange-500/20 text-white'
                : 'border-white/10 bg-white/5 text-white/60 hover:text-white'
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {showFeatured && featured.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/40">
                <Trophy className="size-3.5" />
                Featured Leagues
              </span>
              <div className="flex flex-col gap-2.5">
                {featured.map((l) => (
                  <Link
                    key={l.league_id}
                    href={`/leagues/${l.league_id}`}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0e1114] px-4 py-3 no-underline transition-colors hover:bg-white/5"
                  >
                    <span className="flex size-12 shrink-0 items-center justify-center">
                      {l.logo_url ? (
                        <img src={l.logo_url} alt="" className="size-11 object-contain" />
                      ) : (
                        <span className="size-11 rounded-full border border-white/10" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base font-bold text-white">{l.league_name}</span>
                      {l.country?.flag_square_url && l.country?.name && (
                        <span className="mt-0.5 flex items-center gap-1.5 text-sm text-white/50">
                          <img src={l.country.flag_square_url} alt="" className="h-3.5 w-3.5 object-contain" />
                          {l.country.name}
                        </span>
                      )}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <Accordion type="multiple" className="flex flex-col gap-2.5">
            {(pill === 'all' || pill === 'continental' || pill === 'europe' || pill === 'africa' || pill === 'americas' || pill === 'asia') && (
              <>
                <LeagueGroupAccordion icon={Star} title="European Cups" leagues={byBucket(continentalCups, 'europe')} />
                <LeagueGroupAccordion icon={Star} title="South American & North American Cups" leagues={byBucket(continentalCups, 'americas')} />
                <LeagueGroupAccordion icon={Star} title="African Cups" leagues={byBucket(continentalCups, 'africa')} />
                <LeagueGroupAccordion icon={Star} title="Asian Cups" leagues={byBucket(continentalCups, 'asia')} />
              </>
            )}

            {(pill === 'all' || pill === 'international') && (
              <LeagueGroupAccordion icon={Globe} title="International" leagues={international} />
            )}

            {(pill === 'all' || pill === 'domestic' || pill === 'europe' || pill === 'africa' || pill === 'americas' || pill === 'asia') && (
              <>
                <LeagueGroupAccordion icon={Trophy} title="European Leagues" leagues={byBucket(domestic, 'europe')} />
                <LeagueGroupAccordion icon={Trophy} title="Americas Leagues" leagues={byBucket(domestic, 'americas')} />
                <LeagueGroupAccordion icon={Trophy} title="African & Middle Eastern Leagues" leagues={byBucket(domestic, 'africa')} />
                <LeagueGroupAccordion icon={Trophy} title="Asian & Oceanian Leagues" leagues={byBucket(domestic, 'asia')} />
              </>
            )}

            {(pill === 'all' || pill === 'cups' || pill === 'europe' || pill === 'africa' || pill === 'americas' || pill === 'asia') && (
              <>
                <LeagueGroupAccordion icon={Shield} title="European Domestic Cups" leagues={byBucket(domesticCups, 'europe')} />
                <LeagueGroupAccordion icon={Shield} title="Americas Domestic Cups" leagues={byBucket(domesticCups, 'americas')} />
                <LeagueGroupAccordion icon={Shield} title="African & Middle Eastern Domestic Cups" leagues={byBucket(domesticCups, 'africa')} />
                <LeagueGroupAccordion icon={Shield} title="Asian & Oceanian Domestic Cups" leagues={byBucket(domesticCups, 'asia')} />
              </>
            )}
          </Accordion>

          {filtered.length === 0 && !showFeatured && (
            <p className="py-8 text-center text-sm text-white/40">No leagues match your search.</p>
          )}
        </>
      )}
    </div>
  )
}
